import { useState } from 'react'
import styles from './OnboardingWizard.module.css'
import { useAuth } from '../auth/AuthContext.jsx'

const INDUSTRIES = [
  'Manufacturing', 'Distribution / Logistics', 'Food & Beverage',
  'Construction', 'Healthcare', 'Retail', 'Professional Services', 'Other',
]

const ENTITY_OPTIONS = [
  { id: 'customers',         label: 'Customers' },
  { id: 'products',          label: 'Products' },
  { id: 'inventory',         label: 'Inventory' },
  { id: 'work_orders',       label: 'Work Orders' },
  { id: 'production_runs',   label: 'Production Runs' },
  { id: 'quality_defects',   label: 'Quality / Defects' },
  { id: 'employees',         label: 'Employees' },
  { id: 'equipment',         label: 'Equipment / Machines' },
  { id: 'locations',         label: 'Locations / Plants' },
  { id: 'suppliers',         label: 'Suppliers / Vendors' },
  { id: 'sales_orders',      label: 'Sales Orders' },
  { id: 'purchase_orders',   label: 'Purchase Orders' },
  { id: 'shipments',         label: 'Shipments / Deliveries' },
  { id: 'financials',        label: 'Financial Transactions' },
  { id: 'projects',          label: 'Projects' },
]

const KPI_SUGGESTIONS = {
  'Manufacturing':            ['Yield Rate', 'Scrap Rate', 'OEE', 'On-Time Delivery', 'Downtime Hours', 'Units Produced'],
  'Distribution / Logistics': ['On-Time Delivery', 'Order Fill Rate', 'Inventory Turnover', 'Cost per Shipment', 'Return Rate'],
  'Food & Beverage':          ['Yield Rate', 'Waste %', 'Batch Quality Score', 'On-Time Delivery', 'Inventory Turnover'],
  'Construction':             ['Project Completion %', 'Budget Variance', 'Safety Incidents', 'Labor Hours', 'Change Orders'],
  'Healthcare':               ['Patient Wait Time', 'Bed Utilization', 'Claim Denial Rate', 'Staff-to-Patient Ratio'],
  'Retail':                   ['Sales per Day', 'Inventory Turnover', 'Customer Return Rate', 'Gross Margin', 'Shrinkage'],
  'Professional Services':    ['Billable Hours', 'Project Margin', 'Client Satisfaction', 'Utilization Rate'],
  'Other':                    ['Revenue', 'Cost', 'Customer Count', 'Headcount', 'Gross Margin'],
}

function buildAiContext({ company_name, industry, what_we_do, entities, kpis }) {
  const entLabels = entities
    .map(id => ENTITY_OPTIONS.find(e => e.id === id)?.label)
    .filter(Boolean)
  const parts = []
  if (company_name) parts.push(`${company_name} is a ${industry || 'company'}.`)
  if (what_we_do?.trim()) parts.push(what_we_do.trim())
  if (entLabels.length) parts.push(`Key business data: ${entLabels.join(', ')}.`)
  if (kpis.length) parts.push(`Key metrics: ${kpis.join(', ')}.`)
  return parts.join(' ')
}

export default function OnboardingWizard({ onComplete }) {
  const { apiFetch } = useAuth()
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    company_name: '',
    industry: '',
    what_we_do: '',
    entities: [],
    kpis: [],
    kpiInput: '',
    ai_context: '',
  })

  function set(key, value) {
    setForm(f => {
      const next = { ...f, [key]: value }
      // keep ai_context in sync through steps 1-3; step 4 lets user edit directly
      if (step < 4) next.ai_context = buildAiContext(next)
      return next
    })
  }

  function toggleEntity(id) {
    set('entities', form.entities.includes(id)
      ? form.entities.filter(e => e !== id)
      : [...form.entities, id])
  }

  function toggleKpi(label) {
    set('kpis', form.kpis.includes(label)
      ? form.kpis.filter(k => k !== label)
      : [...form.kpis, label])
  }

  function addKpiInput() {
    const v = form.kpiInput.trim()
    if (v && !form.kpis.includes(v)) {
      set('kpis', [...form.kpis, v])
    }
    setForm(f => ({ ...f, kpiInput: '' }))
  }

  function goNext() {
    if (step === 3) {
      // entering step 4: lock in ai_context from current fields
      setForm(f => ({ ...f, ai_context: buildAiContext(f) }))
    }
    setStep(s => s + 1)
  }

  async function save() {
    setSaving(true)
    try {
      const body = {
        company_name: form.company_name,
        industry: form.industry,
        what_we_do: form.what_we_do,
        entities: form.entities,
        kpis: form.kpis,
        ai_context: form.ai_context,
      }
      const res = await apiFetch('/api/tenant/profile', {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error('save failed')
      const saved = await res.json()

      // Provision per-tenant S3 buckets (idempotent — safe to call on re-onboard)
      await apiFetch('/api/tenant/provision', { method: 'POST' }).catch(() => {})

      onComplete(saved)
    } catch {
      setSaving(false)
    }
  }

  const suggestions = KPI_SUGGESTIONS[form.industry] ?? KPI_SUGGESTIONS['Other']
  const canNext1 = form.company_name.trim() && form.industry
  const canNext2 = form.entities.length > 0
  const canSave  = form.ai_context.trim().length > 10

  return (
    <div className={styles.overlay}>
      <div className={styles.wizard}>
        <div className={styles.steps}>
          {[1, 2, 3, 4].map(n => (
            <div key={n} className={`${styles.stepDot} ${n === step ? styles.stepDotActive : n < step ? styles.stepDotDone : ''}`}>
              {n < step ? '✓' : n}
            </div>
          ))}
          <div className={styles.stepLine} style={{ width: `${((step - 1) / 3) * 100}%` }} />
        </div>

        <div className={styles.body}>
          {step === 1 && (
            <Step1
              form={form}
              set={set}
              onNext={goNext}
              canNext={canNext1}
            />
          )}
          {step === 2 && (
            <Step2
              form={form}
              toggleEntity={toggleEntity}
              onBack={() => setStep(1)}
              onNext={goNext}
              canNext={canNext2}
            />
          )}
          {step === 3 && (
            <Step3
              form={form}
              suggestions={suggestions}
              toggleKpi={toggleKpi}
              onKpiInput={v => setForm(f => ({ ...f, kpiInput: v }))}
              addKpiInput={addKpiInput}
              onBack={() => setStep(2)}
              onNext={goNext}
            />
          )}
          {step === 4 && (
            <Step4
              form={form}
              onContextChange={v => setForm(f => ({ ...f, ai_context: v }))}
              onBack={() => setStep(3)}
              onSave={save}
              saving={saving}
              canSave={canSave}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function Step1({ form, set, onNext, canNext }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <span className={styles.stepTag}>STEP 1 OF 4</span>
        <h2 className={styles.stepTitle}>Tell us about your business</h2>
        <p className={styles.stepSub}>This helps the AI understand your context and give relevant answers from day one.</p>
      </div>

      <div className={styles.fields}>
        <label className={styles.label}>
          Company Name
          <input
            className={styles.input}
            placeholder="e.g. Acme Wind Mfg."
            value={form.company_name}
            onChange={e => set('company_name', e.target.value)}
            autoFocus
          />
        </label>

        <label className={styles.label}>
          Industry
          <select
            className={styles.select}
            value={form.industry}
            onChange={e => set('industry', e.target.value)}
          >
            <option value="">Select an industry…</option>
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
        </label>

        <label className={styles.label}>
          What does your company make or do?
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder="e.g. We manufacture wind turbine rotor blades for OEMs across North America, operating two facilities with three production shifts."
            value={form.what_we_do}
            onChange={e => set('what_we_do', e.target.value)}
          />
        </label>
      </div>

      <div className={styles.actions}>
        <button className={styles.btnPrimary} onClick={onNext} disabled={!canNext}>
          Continue →
        </button>
      </div>
    </div>
  )
}

function Step2({ form, toggleEntity, onBack, onNext, canNext }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <span className={styles.stepTag}>STEP 2 OF 4</span>
        <h2 className={styles.stepTitle}>What does your business track?</h2>
        <p className={styles.stepSub}>Select everything that&apos;s relevant — the AI will focus on these when answering questions.</p>
      </div>

      <div className={styles.checkGrid}>
        {ENTITY_OPTIONS.map(({ id, label }) => (
          <button
            key={id}
            className={`${styles.chip} ${form.entities.includes(id) ? styles.chipActive : ''}`}
            onClick={() => toggleEntity(id)}
          >
            {form.entities.includes(id) && <span className={styles.chipCheck}>✓ </span>}
            {label}
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <button className={styles.btnGhost} onClick={onBack}>← Back</button>
        <button className={styles.btnPrimary} onClick={onNext} disabled={!canNext}>
          Continue →
        </button>
      </div>
    </div>
  )
}

function Step3({ form, suggestions, toggleKpi, onKpiInput, addKpiInput, onBack, onNext }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <span className={styles.stepTag}>STEP 3 OF 4</span>
        <h2 className={styles.stepTitle}>What metrics matter most?</h2>
        <p className={styles.stepSub}>What are the 3–5 numbers you check every day? Select from suggestions or add your own.</p>
      </div>

      <div className={styles.kpiSection}>
        <div className={styles.kpiSuggestLabel}>Suggestions for {form.industry || 'your industry'}</div>
        <div className={styles.checkGrid}>
          {suggestions.map(kpi => (
            <button
              key={kpi}
              className={`${styles.chip} ${form.kpis.includes(kpi) ? styles.chipActive : ''}`}
              onClick={() => toggleKpi(kpi)}
            >
              {form.kpis.includes(kpi) && <span className={styles.chipCheck}>✓ </span>}
              {kpi}
            </button>
          ))}
        </div>

        <div className={styles.kpiAdd}>
          <input
            className={styles.input}
            placeholder="Add a custom KPI…"
            value={form.kpiInput}
            onChange={e => onKpiInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addKpiInput() }}
          />
          <button className={styles.btnSmall} onClick={addKpiInput}>Add</button>
        </div>

        {form.kpis.length > 0 && (
          <div className={styles.kpiPills}>
            {form.kpis.map(k => (
              <span key={k} className={styles.kpiPill}>{k}</span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <button className={styles.btnGhost} onClick={onBack}>← Back</button>
        <button className={styles.btnPrimary} onClick={onNext}>
          Preview & Save →
        </button>
      </div>
    </div>
  )
}

function Step4({ form, onContextChange, onBack, onSave, saving, canSave }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <span className={styles.stepTag}>STEP 4 OF 4</span>
        <h2 className={styles.stepTitle}>Your AI business context</h2>
        <p className={styles.stepSub}>
          This description is prepended to every AI conversation so it always understands your business.
          Edit it to say exactly what&apos;s most useful.
        </p>
      </div>

      <div className={styles.contextCard}>
        <div className={styles.contextLabel}>AI CONTEXT PROMPT</div>
        <textarea
          className={styles.contextArea}
          rows={5}
          value={form.ai_context}
          onChange={e => onContextChange(e.target.value)}
        />
        <div className={styles.contextHint}>
          {form.ai_context.length} characters · You can edit this anytime in Settings → Business Profile
        </div>
      </div>

      <div className={styles.actions}>
        <button className={styles.btnGhost} onClick={onBack}>← Back</button>
        <button className={styles.btnPrimary} onClick={onSave} disabled={saving || !canSave}>
          {saving ? 'Saving…' : 'Start using Structura →'}
        </button>
      </div>
    </div>
  )
}
